package com.downdogapp.client

import com.downdogapp.client.FirstDayOfWeek.MONDAY
import com.downdogapp.client.FirstDayOfWeek.SUNDAY
import com.downdogapp.client.StatType.*
import com.downdogapp.client.api.HistoryItem
import com.downdogapp.client.controllers.start.StatsPage
import com.downdogapp.client.singleton.*
import kotlin.math.roundToInt

object StatsUtil {

  val items
    get() = if (StatsPage.displayAllAppTypes)
      HistoryUtil.items
    else
      HistoryUtil.items.filter { it.appType == App.appType }

  private val Int.durationString
    get() = when {
      this < 60 -> Strings.MINUTES_SHORT(this)
      else -> Strings.HOURS_SHORT(this / 60)
    }

  private val HistoryItem.yearMonthDay
    get() =
      YearMonthDay.fromTimestampSeconds(this.timestamp.seconds)

  val weeklyGoal get() = UserPrefs[Key.WeeklyGoal] ?: 3

  val firstDayOfWeek
    get() =
      UserPrefs[Key.FirstDayOfWeekId]?.let { FirstDayOfWeek.fromId(it) }
      ?: if (MANIFEST.defaultFirstDayOfWeekIsMonday) MONDAY else SUNDAY

  fun getDisplayStringForStat(statType: StatType, yearMonth: YearMonth) = when (statType) {
    TOTAL_PRACTICES -> items.size.toString()
    MONTHLY_PRACTICES -> getNumPracticesInMonth(items, yearMonth).toString()
    TOTAL_TIME_PRACTICED -> getTotalTimePracticed(items).durationString
    MONTHLY_TIME_PRACTICED -> getTimePracticedInMonth(items, yearMonth).durationString
    PRACTICE_STREAK -> getDailyPracticeStreak(items).toString()
    GOAL_STREAK -> getGoalStreak(items, firstDayOfWeek, weeklyGoal).toString()
  }

  fun getStatDescription(statType: StatType, yearMonth: YearMonth) = when (statType) {
    TOTAL_PRACTICES -> Strings.TOTAL_PRACTICES_CAPITALIZED
    MONTHLY_PRACTICES -> Strings.PRACTICES_IN_MONTH(yearMonth.monthNameShort)
    TOTAL_TIME_PRACTICED -> Strings.TOTAL_TIME_CAPITALIZED
    MONTHLY_TIME_PRACTICED -> Strings.MONTHLY_TIME(yearMonth.monthNameShort)
    PRACTICE_STREAK -> Strings.PRACTICE_STREAK_NOT_CAPITALIZED
    GOAL_STREAK -> Strings.GOAL_STREAK_CAPITALIZED
  }

  val practicesThisWeek
    get() =
      getNumPracticesThisWeek(items, firstDayOfWeek)

  private fun getNumPracticesThisWeek(historyItems: List<HistoryItem>, firstDayOfWeek: FirstDayOfWeek): Int {
    val beginningOfWeek = YearMonthDay.today.getStartOfWeek(firstDayOfWeek)
    return historyItems.takeWhile { it.yearMonthDay >= beginningOfWeek }.size
  }

  private fun getTotalTimePracticed(historyItems: List<HistoryItem>) =
    historyItems
      .sumOf {
        it.totalTime.minutes * it.proportionCompleted
      }
      .roundToInt()

  private fun getPracticesInMonth(historyItems: List<HistoryItem>, activeYearMonth: YearMonth) =
    historyItems.filter {
      it.yearMonthDay.yearMonth == activeYearMonth
    }

  private fun getNumPracticesInMonth(historyItems: List<HistoryItem>, activeYearMonth: YearMonth) =
    getPracticesInMonth(historyItems, activeYearMonth).size

  private fun getTimePracticedInMonth(historyItems: List<HistoryItem>, activeYearMonth: YearMonth) =
    getPracticesInMonth(historyItems, activeYearMonth)
      .sumOf {
        it.totalTime.minutes * it.proportionCompleted
      }
      .roundToInt()

  private fun getGoalStreak(historyItems: List<HistoryItem>, firstDayOfWeek: FirstDayOfWeek, weeklyGoal: Int): Int {
    var streak = if (getNumPracticesThisWeek(historyItems, firstDayOfWeek) >= weeklyGoal) 1 else 0
    var endOfWeek = YearMonthDay.today.getStartOfWeek(firstDayOfWeek)
    var beginningOfWeek = endOfWeek.previousWeek()
    var practicesInCurrentWeek = 0
    for (historyItem in historyItems) {
      if (beginningOfWeek <= historyItem.yearMonthDay && historyItem.yearMonthDay < endOfWeek) {
        practicesInCurrentWeek++
        if (practicesInCurrentWeek >= weeklyGoal) {
          endOfWeek = beginningOfWeek
          beginningOfWeek = beginningOfWeek.previousWeek()
          practicesInCurrentWeek = 0
          streak++
        }
      } else if (historyItem.yearMonthDay < beginningOfWeek) {
        break
      }
    }
    return streak
  }

  private fun getDailyPracticeStreak(historyItems: List<HistoryItem>): Int {
    var practiceStreak =
      if (historyItems.isNotEmpty() && historyItems[0].yearMonthDay == YearMonthDay.today) 1 else 0
    var startOfStreak = YearMonthDay.yesterday
    for (historyItem in historyItems) {
      val historyItemDay = historyItem.yearMonthDay
      if (startOfStreak == historyItemDay) {
        startOfStreak = startOfStreak.previousDay()
        practiceStreak++
      } else if (historyItemDay < startOfStreak) {
        break
      }
    }
    return practiceStreak
  }
}
